import {
  Badge,
  Box,
  Button,
  Grid,
  HStack,
  Icon,
  Input,
  Text,
  useDisclosure,
  VStack
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  FiAlertCircle,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiHash,
  FiInfo,
  FiLink,
  FiMapPin,
  FiNavigation,
  FiPlus,
  FiSearch,
  FiShuffle,
  FiTrash2,
  FiTrendingUp
} from "react-icons/fi";
import { ABTestPaths } from "./ABTestPaths";

// Types for the filtered destination system
export interface GeoFilter {
  country?: string;
  city?: string;
}

export interface PlatformFilter {
  platform?: string;
}

export interface DestinationConfig {
  id: string;
  isDefault: boolean;
  filters: {
    geo?: GeoFilter;
    platform?: PlatformFilter;
  };
  destinationType: "single" | "ab-test" | "variant" | "custom";
  destinationPath?: string;
  destinationVariant?: string;
  customRedirectUrl?: string;
  abTestPaths?: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule?: {
    enabled: boolean;
    startDate: string;
    endDate: string;
  };
  isExpanded?: boolean;
}

interface FilteredDestinationSectionProps {
  domain: string;
  destinations: DestinationConfig[];
  onChange: (destinations: DestinationConfig[]) => void;
  disabled?: boolean;
}

// Available countries and cities for geo targeting
const geoOptions = {
  countries: [
    { value: "US", label: "United States" },
    { value: "CA", label: "Canada" },
    { value: "GB", label: "United Kingdom" },
    { value: "AU", label: "Australia" },
    { value: "DE", label: "Germany" },
    { value: "FR", label: "France" },
    { value: "ES", label: "Spain" },
    { value: "IT", label: "Italy" },
    { value: "NL", label: "Netherlands" },
    { value: "BR", label: "Brazil" },
    { value: "MX", label: "Mexico" },
    { value: "JP", label: "Japan" },
    { value: "CN", label: "China" },
    { value: "IN", label: "India" }
  ],
  cities: {
    US: [
      { value: "new-york", label: "New York" },
      { value: "los-angeles", label: "Los Angeles" },
      { value: "chicago", label: "Chicago" },
      { value: "houston", label: "Houston" },
      { value: "phoenix", label: "Phoenix" },
      { value: "philadelphia", label: "Philadelphia" },
      { value: "san-antonio", label: "San Antonio" },
      { value: "san-diego", label: "San Diego" },
      { value: "dallas", label: "Dallas" },
      { value: "san-francisco", label: "San Francisco" },
      { value: "austin", label: "Austin" },
      { value: "seattle", label: "Seattle" },
      { value: "denver", label: "Denver" },
      { value: "miami", label: "Miami" },
      { value: "atlanta", label: "Atlanta" },
      { value: "boston", label: "Boston" }
    ],
    CA: [
      { value: "toronto", label: "Toronto" },
      { value: "montreal", label: "Montreal" },
      { value: "vancouver", label: "Vancouver" },
      { value: "calgary", label: "Calgary" },
      { value: "ottawa", label: "Ottawa" },
      { value: "edmonton", label: "Edmonton" }
    ],
    GB: [
      { value: "london", label: "London" },
      { value: "manchester", label: "Manchester" },
      { value: "birmingham", label: "Birmingham" },
      { value: "liverpool", label: "Liverpool" },
      { value: "edinburgh", label: "Edinburgh" },
      { value: "glasgow", label: "Glasgow" }
    ]
    // Add more cities for other countries as needed
  }
};

// Available advertising platforms
const platformOptions = [
  { value: "facebook", label: "Facebook Ads" },
  { value: "instagram", label: "Instagram Ads" },
  { value: "google-ads", label: "Google Ads" },
  { value: "google-search", label: "Google Search" },
  { value: "youtube", label: "YouTube Ads" },
  { value: "tiktok", label: "TikTok Ads" },
  { value: "linkedin", label: "LinkedIn Ads" },
  { value: "twitter", label: "Twitter Ads" },
  { value: "pinterest", label: "Pinterest Ads" },
  { value: "snapchat", label: "Snapchat Ads" },
  { value: "reddit", label: "Reddit Ads" },
  { value: "bing", label: "Bing Ads" },
  { value: "amazon", label: "Amazon Ads" },
  { value: "spotify", label: "Spotify Ads" }
];

// Available paths for dropdowns
const availablePaths = [
  {
    value: "/seller",
    label: "/seller",
    description: "Seller landing page"
  },
  {
    value: "/buyer",
    label: "/buyer",
    description: "Buyer landing page"
  }
];

// Available website variants
const availableVariants = [
  {
    value: "241211212116",
    label: "241211212116",
    description: "Seller - www/zown-ca-lts/seller/241211212116"
  },
  {
    value: "49504711948",
    label: "49504711948",
    description: "Buyer - www/zown-ca-lts/airbnb-inspired-page/49504711948"
  }
];

// SearchableDropdown Component
interface SearchableDropdownProps {
  value: string;
  placeholder: string;
  options: any[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  disabled: boolean;
  isMonospace?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  placeholder,
  options,
  isOpen,
  onToggle,
  onSelect,
  searchQuery,
  onSearchChange,
  disabled,
  isMonospace = false
}) => (
  <Box position="relative">
    <Box
      as="button"
      w="100%"
      p={3}
      bg="white"
      border="1px solid"
      borderColor={isOpen ? "blue.500" : "gray.300"}
      borderRadius="md"
      textAlign="left"
      fontSize="md"
      aria-disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
          onToggle();
        }
      }}
      _hover={{ borderColor: disabled ? "gray.300" : "gray.400" }}
      _focus={{
        borderColor: disabled ? "gray.300" : "blue.500",
        boxShadow: disabled
          ? "none"
          : "0 0 0 1px var(--chakra-colors-blue-500)",
        outline: "none"
      }}
      _disabled={{
        opacity: 0.6,
        cursor: "not-allowed",
        bg: "gray.50"
      }}
    >
      <HStack justify="space-between">
        <Text
          color={value ? "black" : "gray.500"}
          fontFamily={isMonospace && value ? "mono" : "inherit"}
          fontSize="sm"
        >
          {value || placeholder}
        </Text>
        <Icon
          as={FiChevronDown}
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
          transition="transform 0.2s"
          color="gray.500"
        />
      </HStack>
    </Box>

    {isOpen && (
      <Box
        position="absolute"
        top="100%"
        left={0}
        right={0}
        mt={1}
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        shadow="lg"
        zIndex={1000}
        maxH="400px"
        overflow="hidden"
      >
        <Box p={3} borderBottom="1px solid" borderColor="gray.200">
          <HStack>
            <Icon as={FiSearch} color="gray.400" />
            <Input
              placeholder={`Search ${isMonospace ? "variants" : "paths"}...`}
              value={searchQuery}
              onChange={(e) => {
                e.stopPropagation();
                onSearchChange(e.target.value);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              size="sm"
              border="none"
              _focus={{
                outline: "none",
                boxShadow: "none"
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </HStack>
        </Box>

        <Box maxH="300px" overflowY="auto">
          {options.length > 0 ? (
            options.map((option: any) => (
              <Box
                key={option.value}
                as="button"
                w="100%"
                p={3}
                textAlign="left"
                bg={value === option.value ? "blue.50" : "white"}
                _hover={{ bg: "gray.50" }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(option.value);
                }}
                onMouseDown={(e) => e.preventDefault()}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <HStack justify="space-between" align="start">
                  <VStack align="start" gap={0}>
                    <Text
                      fontSize="sm"
                      fontWeight={value === option.value ? "medium" : "normal"}
                      fontFamily={isMonospace ? "mono" : "inherit"}
                    >
                      {option.label}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {option.description}
                    </Text>
                  </VStack>
                  {value === option.value && (
                    <Icon as={FiCheck} color="blue.500" />
                  )}
                </HStack>
              </Box>
            ))
          ) : (
            <Box p={4} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                No {isMonospace ? "variants" : "paths"} found matching "
                {searchQuery}"
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    )}
  </Box>
);

// URL validation function
const validateUrl = (url: string): { isValid: boolean; error?: string } => {
  if (!url) {
    return { isValid: false, error: "URL is required" };
  }

  try {
    const urlObj = new URL(url);

    if (!["http:", "https:"].includes(urlObj.protocol)) {
      return {
        isValid: false,
        error: "URL must start with http:// or https://"
      };
    }

    if (!urlObj.hostname || urlObj.hostname.length < 3) {
      return { isValid: false, error: "Invalid domain name" };
    }

    if (
      urlObj.hostname.includes("..") ||
      urlObj.hostname.startsWith(".") ||
      urlObj.hostname.endsWith(".")
    ) {
      return { isValid: false, error: "Invalid domain format" };
    }

    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      error:
        "Invalid URL format. Please enter a valid URL (e.g., https://example.com)"
    };
  }
};

// Individual Destination Component
const DestinationItem: React.FC<{
  destination: DestinationConfig;
  index: number;
  domain: string;
  onUpdate: (updatedDestination: DestinationConfig) => void;
  onDelete: () => void;
  disabled?: boolean;
}> = ({ destination, index, domain, onUpdate, onDelete, disabled = false }) => {
  const [pathSearchQuery, setPathSearchQuery] = useState("");
  const [isPathDropdownOpen, setIsPathDropdownOpen] = useState(false);
  const [variantSearchQuery, setVariantSearchQuery] = useState("");
  const [isVariantDropdownOpen, setIsVariantDropdownOpen] = useState(false);
  const [urlValidation, setUrlValidation] = useState<{
    isValid: boolean;
    error?: string;
  }>({ isValid: true });
  const [abTestDropdowns, setAbTestDropdowns] = useState<{
    [key: string]: { isOpen: boolean; searchQuery: string };
  }>({});
  const { open: isOpen, onToggle } = useDisclosure({
    defaultOpen: destination.isDefault || destination.isExpanded || index === 0
  });

  // Get main domain from tracking domain
  const getMainDomain = (trackingDomain: string) => {
    if (trackingDomain.includes("zown.ca")) {
      return "zown.ca";
    } else if (trackingDomain.includes("zownhomes.com")) {
      return "zownhomes.com";
    }
    return "zownhomes.com";
  };

  // Filter paths based on search query
  const filteredPaths = availablePaths.filter(
    (path) =>
      path.value.toLowerCase().includes(pathSearchQuery.toLowerCase()) ||
      path.description.toLowerCase().includes(pathSearchQuery.toLowerCase())
  );

  // Filter variants based on search query
  const filteredVariants = availableVariants.filter(
    (variant) =>
      variant.value.toLowerCase().includes(variantSearchQuery.toLowerCase()) ||
      variant.description
        .toLowerCase()
        .includes(variantSearchQuery.toLowerCase())
  );

  // Get available cities based on selected country
  const getAvailableCities = () => {
    const country = destination.filters?.geo?.country;
    if (
      country &&
      geoOptions.cities[country as keyof typeof geoOptions.cities]
    ) {
      return geoOptions.cities[country as keyof typeof geoOptions.cities];
    }
    return [];
  };

  // Generate filter summary text
  const getFilterSummary = () => {
    const parts = [];

    if (destination.filters?.geo?.country) {
      const country = geoOptions.countries.find(
        (c) => c.value === destination.filters.geo?.country
      );
      if (destination.filters?.geo?.city) {
        const cities = getAvailableCities();
        const city = cities.find(
          (c) => c.value === destination.filters.geo?.city
        );
        parts.push(
          `${city?.label || destination.filters.geo.city}, ${country?.label || destination.filters.geo.country}`
        );
      } else {
        parts.push(country?.label || destination.filters.geo.country);
      }
    }

    if (destination.filters?.platform?.platform) {
      const platform = platformOptions.find(
        (p) => p.value === destination.filters.platform?.platform
      );
      parts.push(platform?.label || destination.filters.platform.platform);
    }

    return parts.length > 0 ? parts.join(" • ") : "All Traffic";
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is inside any dropdown for this destination
      const isInsidePathDropdown = target.closest(
        `[data-path-dropdown="data-path-dropdown-${destination.id}"]`
      );
      const isInsideVariantDropdown = target.closest(
        `[data-variant-dropdown="data-variant-dropdown-${destination.id}"]`
      );

      // Check if click is inside any A/B test dropdown
      const isInsideABDropdown = Object.keys(abTestDropdowns).some((pathId) =>
        target.closest(`[data-ab-dropdown="${pathId}"]`)
      );

      if (isPathDropdownOpen && !isInsidePathDropdown) {
        setIsPathDropdownOpen(false);
        setPathSearchQuery("");
      }

      if (isVariantDropdownOpen && !isInsideVariantDropdown) {
        setIsVariantDropdownOpen(false);
        setVariantSearchQuery("");
      }

      // Close A/B test dropdowns if clicked outside
      if (!isInsideABDropdown) {
        Object.entries(abTestDropdowns).forEach(([pathId, dropdown]) => {
          if (dropdown.isOpen) {
            setAbTestDropdowns((prev) => ({
              ...prev,
              [pathId]: { ...prev[pathId], isOpen: false, searchQuery: "" }
            }));
          }
        });
      }
    };

    // Add a small delay to prevent immediate closing when opening
    const hasOpenDropdowns =
      isPathDropdownOpen ||
      isVariantDropdownOpen ||
      Object.values(abTestDropdowns).some((d) => d.isOpen);

    if (hasOpenDropdowns) {
      const timer = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [
    isPathDropdownOpen,
    isVariantDropdownOpen,
    destination.id,
    abTestDropdowns
  ]);

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor={destination.isDefault ? "blue.200" : "gray.200"}
      rounded="lg"
      overflow="visible"
      position="relative"
    >
      {/* Header */}
      <Box
        p={4}
        bg={destination.isDefault ? "blue.50" : "gray.50"}
        borderBottom={isOpen ? "1px solid" : "none"}
        borderColor={destination.isDefault ? "blue.100" : "gray.200"}
        borderTopRadius="lg"
        borderBottomRadius={isOpen ? "none" : "lg"}
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: destination.isDefault ? "blue.100" : "gray.100" }}
        transition="all 0.2s"
      >
        <HStack justify="space-between">
          <HStack gap={3}>
            <Icon
              as={isOpen ? FiChevronUp : FiChevronDown}
              color={destination.isDefault ? "blue.600" : "gray.600"}
            />
            <VStack align="start" gap={0}>
              <HStack gap={2}>
                <Text fontWeight="semibold" fontSize="sm">
                  {destination.isDefault
                    ? "Default Destination"
                    : `Destination ${index}`}
                </Text>
                {destination.isDefault && (
                  <Badge colorScheme="blue" size="sm">
                    Default
                  </Badge>
                )}
              </HStack>
              <Text fontSize="xs" color="gray.600">
                {getFilterSummary()}
              </Text>
            </VStack>
          </HStack>
          {!destination.isDefault && (
            <Button
              aria-label="Delete destination"
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={disabled}
            >
              <Icon as={FiTrash2} />
            </Button>
          )}
        </HStack>
      </Box>

      {/* Content */}
      {isOpen && (
        <Box p={4} position="relative" overflow="visible">
          <VStack
            gap={4}
            align="stretch"
            position="relative"
            overflow="visible"
          >
            {/* Filters Section */}
            {!destination.isDefault && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={3}>
                  Target Filters
                </Text>

                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                  {/* Geo Targeting */}
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      mb={1}
                      color="gray.700"
                    >
                      <HStack gap={1}>
                        <Icon as={FiMapPin} boxSize={3} />
                        <Text>Country</Text>
                      </HStack>
                    </Text>
                    <Box position="relative">
                      <select
                        value={destination.filters?.geo?.country || ""}
                        onChange={(e) => {
                          const newDestination = {
                            ...destination,
                            filters: {
                              ...destination.filters,
                              geo: {
                                ...destination.filters?.geo,
                                country: e.target.value,
                                city: undefined // Reset city when country changes
                              }
                            }
                          };
                          onUpdate(newDestination);
                        }}
                        disabled={disabled}
                        className="custom-select"
                        style={{
                          width: "100%",
                          backgroundColor: disabled ? "#F7FAFC" : "white",
                          border: "1px solid #CBD5E0",
                          borderRadius: "0.375rem",
                          padding: "0.375rem 0.75rem",
                          fontSize: "0.875rem",
                          outline: "none",
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.6 : 1,
                          appearance: "none",
                          paddingRight: "2rem"
                        }}
                      >
                        <option value="">Select country...</option>
                        {geoOptions.countries.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                      <Icon
                        as={FiChevronDown}
                        position="absolute"
                        right="0.5rem"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                        fontSize="sm"
                      />
                    </Box>
                  </Box>

                  {/* City Targeting */}
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      mb={1}
                      color="gray.700"
                    >
                      <HStack gap={1}>
                        <Icon as={FiMapPin} boxSize={3} />
                        <Text>City (Optional)</Text>
                      </HStack>
                    </Text>
                    <Box position="relative">
                      <select
                        value={destination.filters?.geo?.city || ""}
                        onChange={(e) => {
                          const newDestination = {
                            ...destination,
                            filters: {
                              ...destination.filters,
                              geo: {
                                ...destination.filters?.geo,
                                city: e.target.value
                              }
                            }
                          };
                          onUpdate(newDestination);
                        }}
                        disabled={
                          disabled || !destination.filters?.geo?.country
                        }
                        className="custom-select"
                        style={{
                          width: "100%",
                          backgroundColor:
                            disabled || !destination.filters?.geo?.country
                              ? "#F7FAFC"
                              : "white",
                          border: "1px solid #CBD5E0",
                          borderRadius: "0.375rem",
                          padding: "0.375rem 0.75rem",
                          fontSize: "0.875rem",
                          outline: "none",
                          opacity:
                            disabled || !destination.filters?.geo?.country
                              ? 0.6
                              : 1,
                          cursor:
                            disabled || !destination.filters?.geo?.country
                              ? "not-allowed"
                              : "pointer",
                          appearance: "none",
                          paddingRight: "2rem"
                        }}
                      >
                        <option value="">
                          {destination.filters?.geo?.country
                            ? "Select city..."
                            : "Select country first"}
                        </option>
                        {getAvailableCities().map((city) => (
                          <option key={city.value} value={city.value}>
                            {city.label}
                          </option>
                        ))}
                      </select>
                      <Icon
                        as={FiChevronDown}
                        position="absolute"
                        right="0.5rem"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                        fontSize="sm"
                      />
                    </Box>
                  </Box>

                  {/* Platform Targeting */}
                  <Box gridColumn="span 2">
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      mb={1}
                      color="gray.700"
                    >
                      <HStack gap={1}>
                        <Icon as={FiTrendingUp} boxSize={3} />
                        <Text>Advertising Platform</Text>
                      </HStack>
                    </Text>
                    <Box position="relative">
                      <select
                        value={destination.filters?.platform?.platform || ""}
                        onChange={(e) => {
                          const newDestination = {
                            ...destination,
                            filters: {
                              ...destination.filters,
                              platform: {
                                platform: e.target.value
                              }
                            }
                          };
                          onUpdate(newDestination);
                        }}
                        disabled={disabled}
                        className="custom-select"
                        style={{
                          width: "100%",
                          backgroundColor: disabled ? "#F7FAFC" : "white",
                          border: "1px solid #CBD5E0",
                          borderRadius: "0.375rem",
                          padding: "0.375rem 0.75rem",
                          fontSize: "0.875rem",
                          outline: "none",
                          opacity: disabled ? 0.6 : 1,
                          cursor: disabled ? "not-allowed" : "pointer",
                          appearance: "none",
                          paddingRight: "2rem"
                        }}
                      >
                        <option value="">Select platform...</option>
                        {platformOptions.map((platform) => (
                          <option key={platform.value} value={platform.value}>
                            {platform.label}
                          </option>
                        ))}
                      </select>
                      <Icon
                        as={FiChevronDown}
                        position="absolute"
                        right="0.5rem"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                        fontSize="sm"
                      />
                    </Box>
                  </Box>
                </Grid>

                <Box borderBottom="1px solid" borderColor="gray.200" my={4} />
              </Box>
            )}

            {/* Destination Type Selection */}
            <Box>
              <HStack mb={3}>
                <Text fontSize="sm" fontWeight="medium">
                  Destination Type
                </Text>
              </HStack>
              <HStack gap={2} wrap="wrap">
                <Button
                  type="button"
                  size="sm"
                  variant={
                    destination.destinationType === "single"
                      ? "solid"
                      : "outline"
                  }
                  colorScheme={
                    destination.destinationType === "single" ? "blue" : "gray"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    onUpdate({ ...destination, destinationType: "single" });
                  }}
                  disabled={disabled}
                >
                  <HStack gap={1}>
                    <Icon as={FiLink} boxSize={4} />
                    <Text>Single Path</Text>
                  </HStack>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={
                    destination.destinationType === "ab-test"
                      ? "solid"
                      : "outline"
                  }
                  colorScheme={
                    destination.destinationType === "ab-test" ? "blue" : "gray"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    const updatedDestination = {
                      ...destination,
                      destinationType: "ab-test" as const,
                      abTestPaths:
                        destination.abTestPaths &&
                        destination.abTestPaths.length >= 2
                          ? destination.abTestPaths
                          : [
                              {
                                id: Date.now().toString(),
                                type: "path" as const,
                                value: "",
                                percentage: 50
                              },
                              {
                                id: (Date.now() + 1).toString(),
                                type: "path" as const,
                                value: "",
                                percentage: 50
                              }
                            ]
                    };
                    onUpdate(updatedDestination);
                  }}
                  disabled={disabled}
                >
                  <HStack gap={1}>
                    <Icon as={FiShuffle} boxSize={4} />
                    <Text>A/B Test</Text>
                  </HStack>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={
                    destination.destinationType === "variant"
                      ? "solid"
                      : "outline"
                  }
                  colorScheme={
                    destination.destinationType === "variant" ? "blue" : "gray"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    onUpdate({ ...destination, destinationType: "variant" });
                  }}
                  disabled={disabled}
                >
                  <HStack gap={1}>
                    <Icon as={FiHash} boxSize={4} />
                    <Text>Variant ID</Text>
                  </HStack>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={
                    destination.destinationType === "custom"
                      ? "solid"
                      : "outline"
                  }
                  colorScheme={
                    destination.destinationType === "custom" ? "blue" : "gray"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    onUpdate({ ...destination, destinationType: "custom" });
                  }}
                  disabled={disabled}
                >
                  <HStack gap={1}>
                    <Icon as={FiExternalLink} boxSize={4} />
                    <Text>Custom URL</Text>
                  </HStack>
                </Button>
              </HStack>
            </Box>

            {/* Single Path Configuration */}
            {destination.destinationType === "single" && (
              <Box>
                <HStack mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Website Path
                  </Text>
                </HStack>
                <HStack position="relative">
                  <Text fontSize="sm" color="gray.600" minW="fit-content">
                    https://{getMainDomain(domain)}
                  </Text>
                  <Box
                    position="relative"
                    flex={1}
                    data-path-dropdown={`data-path-dropdown-${destination.id}`}
                    zIndex={isPathDropdownOpen ? 50 : 1}
                  >
                    <SearchableDropdown
                      value={destination.destinationPath || ""}
                      placeholder="Select a path..."
                      options={filteredPaths}
                      isOpen={isPathDropdownOpen}
                      onToggle={() => {
                        setIsPathDropdownOpen(!isPathDropdownOpen);
                        setPathSearchQuery("");
                        setIsVariantDropdownOpen(false);
                      }}
                      onSelect={(value) => {
                        onUpdate({ ...destination, destinationPath: value });
                        setIsPathDropdownOpen(false);
                        setPathSearchQuery("");
                      }}
                      searchQuery={pathSearchQuery}
                      onSearchChange={setPathSearchQuery}
                      disabled={disabled}
                    />
                  </Box>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Select from available paths or search by path name
                </Text>
              </Box>
            )}

            {/* Variant Configuration */}
            {destination.destinationType === "variant" && (
              <Box>
                <HStack mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Website Variant ID
                  </Text>
                </HStack>
                <Box
                  position="relative"
                  data-variant-dropdown={`data-variant-dropdown-${destination.id}`}
                  zIndex={isVariantDropdownOpen ? 50 : 1}
                >
                  <SearchableDropdown
                    value={destination.destinationVariant || ""}
                    placeholder="Select a variant..."
                    options={filteredVariants}
                    isOpen={isVariantDropdownOpen}
                    onToggle={() => {
                      setIsVariantDropdownOpen(!isVariantDropdownOpen);
                      setVariantSearchQuery("");
                      setIsPathDropdownOpen(false);
                    }}
                    onSelect={(value) => {
                      onUpdate({ ...destination, destinationVariant: value });
                      setIsVariantDropdownOpen(false);
                      setVariantSearchQuery("");
                    }}
                    searchQuery={variantSearchQuery}
                    onSearchChange={setVariantSearchQuery}
                    disabled={disabled}
                    isMonospace={true}
                  />
                </Box>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Select a specific website variant by its ID
                </Text>
              </Box>
            )}

            {/* A/B Test Configuration */}
            {destination.destinationType === "ab-test" && (
              <VStack gap={4} align="stretch">
                {/* Test Schedule Settings */}
                <Box>
                  <HStack mb={3}>
                    <Text fontSize="sm" fontWeight="medium">
                      Auto-schedule test activation
                    </Text>
                    <Box
                      position="relative"
                      display="inline-block"
                      width="44px"
                      height="24px"
                      bg={
                        destination.abTestSchedule?.enabled
                          ? "blue.500"
                          : "gray.300"
                      }
                      borderRadius="full"
                      transition="all 0.3s"
                      cursor={disabled ? "not-allowed" : "pointer"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!disabled) {
                          const schedule = destination.abTestSchedule || {
                            enabled: false
                          };
                          if (!schedule.enabled) {
                            const now = new Date();
                            const year = now.getFullYear();
                            const month = String(now.getMonth() + 1).padStart(
                              2,
                              "0"
                            );
                            const day = String(now.getDate()).padStart(2, "0");
                            const hours = String(now.getHours()).padStart(
                              2,
                              "0"
                            );
                            const minutes = String(now.getMinutes()).padStart(
                              2,
                              "0"
                            );
                            const currentDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;

                            onUpdate({
                              ...destination,
                              abTestSchedule: {
                                enabled: true,
                                startDate: currentDateTime,
                                endDate: ""
                              }
                            });
                          } else {
                            onUpdate({
                              ...destination,
                              abTestSchedule: {
                                enabled: false,
                                startDate: "",
                                endDate: ""
                              }
                            });
                          }
                        }
                      }}
                      _hover={{
                        bg: destination.abTestSchedule?.enabled
                          ? "blue.600"
                          : "gray.400"
                      }}
                      _after={{
                        content: '""',
                        position: "absolute",
                        width: "20px",
                        height: "20px",
                        borderRadius: "full",
                        bg: "white",
                        top: "2px",
                        left: destination.abTestSchedule?.enabled
                          ? "22px"
                          : "2px",
                        transition: "left 0.3s",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                      }}
                    />
                  </HStack>

                  {destination.abTestSchedule?.enabled && (
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          mb={1}
                          color="gray.700"
                        >
                          Start Date & Time
                        </Text>
                        <Input
                          type="datetime-local"
                          value={destination.abTestSchedule?.startDate || ""}
                          onChange={(e) =>
                            onUpdate({
                              ...destination,
                              abTestSchedule: {
                                enabled:
                                  destination.abTestSchedule?.enabled || true,
                                startDate: e.target.value,
                                endDate:
                                  destination.abTestSchedule?.endDate || ""
                              }
                            })
                          }
                          size="sm"
                          disabled={disabled}
                        />
                      </Box>
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          mb={1}
                          color="gray.700"
                        >
                          End Date & Time (Optional)
                        </Text>
                        <Input
                          type="datetime-local"
                          value={destination.abTestSchedule?.endDate || ""}
                          onChange={(e) =>
                            onUpdate({
                              ...destination,
                              abTestSchedule: {
                                enabled:
                                  destination.abTestSchedule?.enabled || true,
                                startDate:
                                  destination.abTestSchedule?.startDate || "",
                                endDate: e.target.value
                              }
                            })
                          }
                          size="sm"
                          disabled={disabled}
                        />
                      </Box>
                    </Grid>
                  )}
                </Box>

                {/* Test Paths */}
                <Box>
                  <ABTestPaths
                    paths={
                      destination.abTestPaths || [
                        {
                          id: Date.now().toString(),
                          type: "path",
                          value: "",
                          percentage: 50
                        },
                        {
                          id: (Date.now() + 1).toString(),
                          type: "path",
                          value: "",
                          percentage: 50
                        }
                      ]
                    }
                    onChange={(paths) =>
                      onUpdate({ ...destination, abTestPaths: paths })
                    }
                    disabled={disabled}
                    domain={domain}
                    availablePaths={availablePaths}
                    availableVariants={availableVariants}
                    dropdownStates={abTestDropdowns}
                    onDropdownChange={(pathId, state) => {
                      setAbTestDropdowns((prev) => ({
                        ...prev,
                        [pathId]: state
                      }));
                    }}
                  />
                </Box>
              </VStack>
            )}

            {/* Custom URL Configuration */}
            {destination.destinationType === "custom" && (
              <Box>
                <HStack mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Custom Redirect URL
                  </Text>
                  {destination.customRedirectUrl && !urlValidation.isValid && (
                    <HStack gap={1}>
                      <Icon as={FiAlertCircle} color="red.500" boxSize={4} />
                      <Text fontSize="xs" color="red.500">
                        {urlValidation.error}
                      </Text>
                    </HStack>
                  )}
                  {destination.customRedirectUrl && urlValidation.isValid && (
                    <HStack gap={1}>
                      <Icon as={FiCheck} color="green.500" boxSize={4} />
                      <Text fontSize="xs" color="green.500">
                        Valid URL
                      </Text>
                    </HStack>
                  )}
                </HStack>
                <Input
                  value={destination.customRedirectUrl || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    onUpdate({ ...destination, customRedirectUrl: value });
                    if (value) {
                      const validation = validateUrl(value);
                      setUrlValidation(validation);
                    } else {
                      setUrlValidation({ isValid: true });
                    }
                  }}
                  placeholder="https://example.com/landing-page"
                  disabled={disabled}
                  size="sm"
                  borderColor={
                    destination.customRedirectUrl && !urlValidation.isValid
                      ? "red.500"
                      : undefined
                  }
                />
                <Text
                  fontSize="xs"
                  color={
                    destination.customRedirectUrl && !urlValidation.isValid
                      ? "red.500"
                      : "gray.500"
                  }
                  mt={2}
                >
                  {destination.customRedirectUrl && !urlValidation.isValid
                    ? urlValidation.error
                    : "Enter the full URL where users will be redirected"}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export const FilteredDestinationSection: React.FC<
  FilteredDestinationSectionProps
> = ({ domain, destinations, onChange, disabled = false }) => {
  // Add a new destination configuration
  const handleAddDestination = () => {
    const newDestination: DestinationConfig = {
      id: `dest-${Date.now()}`,
      isDefault: false,
      filters: {},
      destinationType: "single",
      destinationPath: "",
      abTestPaths: [
        { id: "1", type: "path", value: "", percentage: 50 },
        { id: "2", type: "path", value: "", percentage: 50 }
      ],
      isExpanded: true
    };
    onChange([...destinations, newDestination]);
  };

  // Update a specific destination
  const handleUpdateDestination = (
    index: number,
    updatedDestination: DestinationConfig
  ) => {
    const newDestinations = [...destinations];
    newDestinations[index] = updatedDestination;
    onChange(newDestinations);
  };

  // Delete a destination
  const handleDeleteDestination = (index: number) => {
    const newDestinations = destinations.filter((_, i) => i !== index);
    onChange(newDestinations);
  };

  return (
    <Box bg="white" shadow="sm" rounded="lg" p={6}>
      <HStack mb={4} justify="space-between">
        <HStack>
          <Icon as={FiNavigation} color="blue.500" boxSize={5} />
          <Text fontWeight="semibold" fontSize="lg">
            Destination Configuration
          </Text>
          <Badge colorScheme="red" size="sm">
            Required
          </Badge>
        </HStack>
        <Text fontSize="xs" color="gray.600">
          Configure multiple destinations based on user location or ad platform
        </Text>
      </HStack>

      <VStack gap={4} align="stretch" overflow="visible">
        {/* Info Box */}
        <Box
          p={3}
          bg="blue.50"
          rounded="md"
          borderLeft="3px solid"
          borderColor="blue.400"
        >
          <HStack gap={2} align="start">
            <Icon as={FiInfo} color="blue.600" boxSize={4} mt={0.5} />
            <VStack align="start" gap={1}>
              <Text fontSize="xs" color="blue.800" fontWeight="medium">
                Smart Destination Routing:
              </Text>
              <Text fontSize="xs" color="blue.700">
                • Default destination handles all traffic without specific
                filters
              </Text>
              <Text fontSize="xs" color="blue.700">
                • Add filtered destinations for specific countries, cities, or
                ad platforms
              </Text>
              <Text fontSize="xs" color="blue.700">
                • More specific filters take priority over general ones
              </Text>
              <Text fontSize="xs" color="blue.700">
                • Each destination can be configured with different paths, A/B
                tests, or custom URLs
              </Text>
            </VStack>
          </HStack>
        </Box>

        {/* Destination List */}
        {destinations.map((destination, index) => (
          <DestinationItem
            key={destination.id}
            destination={destination}
            index={index}
            domain={domain}
            onUpdate={(updated) => handleUpdateDestination(index, updated)}
            onDelete={() => handleDeleteDestination(index)}
            disabled={disabled}
          />
        ))}

        {/* Add Destination Button */}
        <Button
          variant="outline"
          colorScheme="blue"
          onClick={handleAddDestination}
          disabled={disabled}
          size="sm"
        >
          <HStack gap={1}>
            <Icon as={FiPlus} />
            <Text>Add Filtered Destination</Text>
          </HStack>
        </Button>
      </VStack>
    </Box>
  );
};

// Add custom styles
if (
  typeof document !== "undefined" &&
  !document.querySelector("#filtered-destination-section-styles")
) {
  const style = document.createElement("style");
  style.id = "filtered-destination-section-styles";
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .animate-spin {
      animation: spin 1s linear infinite;
    }
    .custom-select:focus {
      border-color: #3182CE !important;
      box-shadow: 0 0 0 1px #3182CE !important;
    }
    .custom-select:hover:not(:disabled) {
      border-color: #A0AEC0 !important;
    }
  `;
  document.head.appendChild(style);
}
